"use client";

import { memo } from "react";
import { twMerge } from "tailwind-merge";

import { SmallAudioPlayer } from "@/components/audioPlayers/SmallAudioPlayer";
import { SaveButton } from "@/components/compactArtistDisplay/SaveButton";
import { ShowImageArtistInstrumentModelMixedModel } from "@/components/showImage/ShowImageArtistInstrumentModelMixedModel";
import { Badge } from "@/components/text/Badge";
import { SmallTitle } from "@/components/text/SmallTitle";
import {
  Artist,
  ArtistInstumentSaved,
  Instrument,
  MixedModelConstructedNoDates,
  ModelNoDates,
  UserNoDates,
} from "@/types/types";
import { classNames } from "@/utils/classNames";
import { createMixedModelName } from "@/utils/createMixedModelName";
import { isAllowedArtist } from "@/utils/subscriptionLevels/isAllowedArtist";

function UnmemoizedCompactArtistDisplay({
  artistInstrumentModelMixedModel,
  handleClickArtist,
  user,
  elementType,
  savedArtistsInstrumentsIds,
  whiteTags,
  className,
}: {
  artistInstrumentModelMixedModel:
    | Artist
    | Instrument
    | ModelNoDates
    | MixedModelConstructedNoDates;
  handleClickArtist: (artistId: string) => void;
  user: UserNoDates | null;
  elementType: "li" | "div";
  savedArtistsInstrumentsIds: ArtistInstumentSaved["artistInstrumentId"][];
  whiteTags?: boolean;
  className?: string;
}) {
  const clickHandler = () => {
    handleClickArtist(artistInstrumentModelMixedModel.id);
  };

  let showUpgradeText = false;
  if ("meta" in artistInstrumentModelMixedModel) {
    showUpgradeText = !isAllowedArtist(
      user === null ? null : user.stripeSubscriptionId,
      artistInstrumentModelMixedModel
    );
  }

  const Element = elementType;

  const name =
    "name" in artistInstrumentModelMixedModel ?
      artistInstrumentModelMixedModel.name
    : createMixedModelName(artistInstrumentModelMixedModel);

  return (
    <Element className={twMerge("relative", className)}>
      <div className="group">
        <div className="relative">
          <ShowImageArtistInstrumentModelMixedModel
            artistInstrumentModelMixedModel={artistInstrumentModelMixedModel}
          />
          {"previewUrl" in artistInstrumentModelMixedModel && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 top-0 z-20 flex items-center justify-center">
              <SmallAudioPlayer
                source={artistInstrumentModelMixedModel.previewUrl}
                className="pointer-events-auto"
                blackIcons={false}
              />
            </div>
          )}
          {showUpgradeText && (
            <div className="pointer-events-none absolute bottom-2 left-0 right-0 z-10 flex items-center justify-center">
              <Badge>Upgrade</Badge>
            </div>
          )}
        </div>
        {"meta" in artistInstrumentModelMixedModel &&
          artistInstrumentModelMixedModel.meta.new && (
            <div className="pointer-events-none absolute left-4 top-3 z-10">
              <span className="rounded-md bg-gray-900 bg-opacity-50 px-2 py-1 text-sm font-medium text-white">
                👀 New
              </span>
            </div>
          )}

        <SaveButton
          user={user}
          artistInstrumentModelMixedModel={artistInstrumentModelMixedModel}
          savedArtistsInstrumentsIds={savedArtistsInstrumentsIds}
        />
        <div className="my-1 flex items-center gap-x-2">
          <SmallTitle
            elementType="p"
            className="pointer-events-none block truncate"
          >
            {name}
          </SmallTitle>
        </div>

        {"filters" in artistInstrumentModelMixedModel && (
          <div className="flex flex-wrap gap-1">
            {[
              ...artistInstrumentModelMixedModel.filters.tags.primary,
              ...artistInstrumentModelMixedModel.filters.tags.secondary,
            ].map((tag) => (
              <Badge
                key={tag}
                className={classNames(
                  whiteTags === true ? "bg-white" : "bg-gray-200",
                  "px-1 py-0.5"
                )}
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}
        <button
          className="absolute inset-0 h-full w-full rounded-t-3xl"
          onClick={clickHandler}
          data-tutorial={`artist-${artistInstrumentModelMixedModel.id}`}
        >
          <span className="sr-only">Go to conversion page for {name}</span>
        </button>
      </div>
    </Element>
  );
}

export const CompactArtistDisplay = memo(UnmemoizedCompactArtistDisplay);
